import React, { useEffect, useState, useMemo } from "react";
import "./BookingApproval.css";

/**
 * Align old vs new activities into comparable rows
 */
const alignActivities = (prevActivities, currActivities) => {
    const rows = [];

    // Normalize nulls and sort
    const prev = [...(prevActivities || [])].sort((a, b) =>
        (a.activityId || 0) - (b.activityId || 0)
    );
    const curr = [...(currActivities || [])].sort((a, b) =>
        (a.activityId || 0) - (b.activityId || 0)
    );

    const usedCurr = new Set();

    // Step 1 — Try to pair by exact ID
    prev.forEach((p) => {
        const match = curr.find((c) => c.activityId === p.activityId);
        if (match) {
            usedCurr.add(match.activityId);

            const deleted = match.isDeleted;
            const canceled = match.isCanceled;

            if (deleted || canceled) {
                rows.push({
                    prev: p,
                    curr: match,
                    rowType: "removed",
                    removedType: deleted ? "deleted" : "canceled",
                });
            } else {
                const isSame =
                    p.activityName === match.activityName &&
                    p.personCount === match.personCount &&
                    p.activityDate === match.activityDate &&
                    p.timeFrom === match.timeFrom &&
                    p.timeTo === match.timeTo;

                rows.push({
                    prev: p,
                    curr: match,
                    rowType: isSame ? "matched" : "changed",
                });
            }
        } else {
            // Try soft match (same name/date/time)
            const soft = curr.find(
                (c) =>
                    !usedCurr.has(c.activityId) &&
                    c.activityName === p.activityName &&
                    c.activityDate === p.activityDate &&
                    c.timeFrom === p.timeFrom
            );
            if (soft) {
                usedCurr.add(soft.activityId);
                rows.push({
                    prev: p,
                    curr: soft,
                    rowType: "changed",
                });
            } else {
                rows.push({
                    prev: p,
                    curr: null,
                    rowType: "removed",
                    removedType: p.isCanceled ? "canceled" : "deleted",
                });
            }
        }
    });

    // Step 2 — Add any new ones not matched
    curr.forEach((c) => {
        if (!usedCurr.has(c.activityId)) {
            if (c.isDeleted || c.isCanceled) {
                rows.push({
                    prev: null,
                    curr: c,
                    rowType: "removed",
                    removedType: c.isDeleted ? "deleted" : "canceled",
                });
            } else {
                rows.push({ prev: null, curr: c, rowType: "new" });
            }
        }
    });

    return rows;
};

const ActivityCell = ({ activity, isEmpty, label, isLatest }) => {
    if (isEmpty || !activity) {
        return (
            <div className="text-center py-6">
                <div className="text-gray-300 text-sm font-medium">{label}</div>
                <div className="text-gray-200 text-xs mt-1">Ingen aktivitet</div>
            </div>
        );
    }

    const totalPrice = (activity.pricePerPerson || 0) * (activity.personCount || 0);
    activity.pricePerPerson = activity?.pricePerPerson || 0;
    return (
        <div className="py-3">
            <div className="font-medium items-center mb-2">
                {activity.activityName}
            </div>
            <div className="font-medium items-center mb-2">
                <i className="ri-user-line mr-1.5" />
                <span>{activity.personCount} personer</span>
            </div>
            {activity.activityDate && (
                <div className="font-medium items-center mb-2">
                    <i className="ri-calendar-line mr-1.5 text-gray-500" />
                    {activity.activityDate.split("T")[0]}
                </div>
            )}
            {(activity.timeFrom || activity.timeTo) && (
                <div className="font-medium items-center mb-2">
                    <i className="ri-time-line mr-1.5 text-gray-500" />
                    {activity.timeFrom && activity.timeFrom.slice(0, 5)}
                    {activity.timeTo && ` - ${activity.timeTo.slice(0, 5)}`}
                </div>
            )}
            {activity.pricePerPerson >= 0 && (
                <div className="font-medium items-center mb-2">
                    <i className="ri-money-dollar-circle-line mr-1.5 text-gray-500" />
                    Pris per person: {activity.pricePerPerson} NOK
                </div>
            )}
            {totalPrice >= 0 && (
                <div className="font-medium items-center mb-2">
                    <i className="ri-calculator-line mr-1.5 text-gray-500" />
                    Totalt: {totalPrice} NOK
                </div>
            )}
            {activity?.reason && isLatest && (
                <div
                    className="admin-comment mt-3 flex items-start gap-2 p-2 rounded-md bg-gray-50 border border-gray-200"
                    style={{ minHeight: "60px", maxWidth: "100%" }}
                >
                    <i className="ri-chat-1-line text-purple-600 text-lg mt-1"></i>
                    <div className="flex-1 overflow-hidden">
                        <b className="text-purple-600 block mb-1">Funtlers kommentar:</b>
                        <p className="mb-0 text-gray-700 break-words whitespace-pre-line" style={{ wordBreak: "break-word" }}>
                            {activity.reason}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

const DecisionButton = ({ decision, currentDecision, onClick, icon, label }) => {
    const isActive = currentDecision === decision;
    const baseClasses =
        "flex items-center px-4 py-2 rounded-full font-medium transition-all duration-200 text-sm -sm";
    const variantClasses = isActive
        ? decision === "approved"
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-red-600 text-white hover:bg-red-700"
        : "bg-white  border border-gray-200 hover:border-gray-400";
    return (
        <button onClick={onClick} className={`${baseClasses} ${variantClasses}`}>
            <i className={`${icon} mr-2 text-sm`} />
            {label}
        </button>
    );
};

const RowTypeIndicator = ({ rowType }) => {
    const config = {
        new: { icon: "ri-add-line", color: "text-green-700 bg-green-100", label: "Ny" },
        removed: { icon: "ri-subtract-line", color: "text-red-700 bg-red-100", label: "Fjernet" },
        changed: { icon: "ri-edit-line", color: "text-orange-700 bg-orange-100", label: "Endret" },
        matched: { icon: "ri-check-line", color: " bg-gray-100", label: "Uendret" },
    };
    const { icon, color, label } = config[rowType] || config.matched;
    return (
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-ms font-medium ${color}`}>
            <i className={`${icon} mr-1.5 text-xs`} />
            {label}
        </div>
    );
};

export default function BookingApproval({ bookingData, onSave, onCancel }) {
    const [rows, setRows] = useState([]);
    const [decisions, setDecisions] = useState({});
    const [globalComment, setGlobalComment] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [showRemoved, setShowRemoved] = useState(false);

    useEffect(() => {
        if (!bookingData) return;
        const prevActivities = bookingData.previousActivities || [];
        const currActivities = bookingData.activities || [];
        const aligned = alignActivities(prevActivities, currActivities);
        const initDecisions = {};
        aligned.forEach((row) => {
            const key = row.curr?.activityId || row.prev?.activityId;
            if (key) {
                initDecisions[key] = {
                    decision: row.rowType === "matched" ? "approved" : "pending",
                    comment: "",
                };
            }
        });
        setRows(aligned);
        setDecisions(initDecisions);
        setError("");
        setGlobalComment("");
    }, [bookingData]);

    const stats = useMemo(() => {
        const active = rows.filter((r) => r.rowType !== "removed");
        const total = active.length;
        const approved = Object.values(decisions).filter((d) => d.decision === "approved").length;
        const rejected = Object.values(decisions).filter((d) => d.decision === "rejected").length;
        const pending = total - approved - rejected;
        return { total, approved, rejected, pending };
    }, [rows, decisions]);

    const setDecision = (id, decision) => {
        setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], decision } }));
    };

    const setComment = (id, comment) => {
        setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], comment } }));
    };

    const bulkAction = (mode) => {
        setDecisions((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((id) => {
                updated[id].decision = mode === "approve" ? "approved" : "rejected";
                if (mode === "approve") updated[id].comment = "";
            });
            return updated;
        });
    };

    const handleSave = async () => {
        setError("");
        bookingData.globalComment = globalComment;
        (bookingData.activities || []).forEach((activity) => {
            const decisionData = decisions[activity.activityId] || { decision: "pending", comment: "" };
            activity.isApproved = decisionData.decision === "approved";
            activity.isCanceled = decisionData.decision === "rejected";
            activity.reason = decisionData.decision === "rejected" ? decisionData.comment : "";
        });
        const errors = (bookingData.activities || [])
            .filter((a) => a.isCanceled && !a.reason?.trim())
            .map((a) => `"${a.activityName}" krever en begrunnelse ved avvisning.`);
        if (errors.length) {
            setError(errors.join("\n"));
            return;
        }
        setSaving(true);
        try {
            await new Promise((res) => setTimeout(res, 800));
            onSave && onSave(bookingData);
        } catch (err) {
            setError("Feil under lagring: " + (err?.message || err));
        } finally {
            setSaving(false);
        }
    };

    if (!bookingData) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="">Ingen booking data tilgjengelig</div>
            </div>
        );
    }

    const activeRows = rows.filter((r) => r.rowType !== "removed");
    const removedRows = rows.filter((r) => r.rowType === "removed");

    return (
        <div className="min-h-screen bg-gray-50 p-2">
            <div className="max-w-7xl mx-auto px-6 py-2">
                {/* Header */}
                {/* Header */}
                <div className="mb-6 p-6 ">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Aktiviteter */}
                        <div className="flex flex-col items-start bg-white rounded-lg p-4 shadow border border-gray-100">
                            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                                Aktiviteter
                            </div>
                            <div className="text-3xl font-semibold text-gray-800">
                                {stats.total}
                            </div>
                        </div>

                        {/* Godkjent */}
                        <div className="flex flex-col items-start bg-white rounded-lg p-4 shadow border border-gray-100">
                            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                                Godkjent
                            </div>
                            <div className="text-3xl font-semibold text-green-600">
                                {Math.min(stats.approved, stats.total)}
                            </div>
                        </div>

                        {/* Avventer */}
                        <div className="flex flex-col items-start bg-white rounded-lg p-4 shadow border border-gray-100">
                            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                                Avventer
                            </div>
                            <div className="text-3xl font-semibold text-orange-500">
                                {stats.pending <= 0 ? 0 : stats.pending}
                            </div>
                        </div>
                    </div>
                </div>


                {/* Activities Table */}
                <div className="mb-16 overflow-hidden bg-white">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-orange-50 to-purple-50 border-b border-gray-200">
                                <th className="text-center py-5 px-6 text-ms font-semibold uppercase tracking-wider w-28">Status</th>
                                <th className="text-center py-5 px-6 text-ms font-semibold uppercase tracking-wider">Opprinnelig aktivitet</th>
                                <th className="text-center py-5 px-6 text-ms font-semibold uppercase tracking-wider">Ny versjon</th>
                                <th className="text-center py-5 px-6 text-ms font-semibold uppercase tracking-wider w-80">Beslutning</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Removed Group */}
                            {removedRows.length > 0 && (
                                <>
                                    <tr
                                        className="bg-red-50 cursor-pointer hover:bg-red-100 transition"
                                        onClick={() => setShowRemoved(!showRemoved)}
                                    >
                                        <td colSpan={4} className="py-4 px-6 font-medium text-red-700">
                                            <i className={`ri-arrow-${showRemoved ? "down" : "right"}-s-line mr-2`} />
                                            {removedRows.length} aktivitet(er) kansellert
                                        </td>
                                    </tr>
                                    {showRemoved &&
                                        removedRows.map((row) => {
                                            const key = row.curr?.activityId || row.prev?.activityId;
                                            if (!key) return null;
                                            return (
                                                <tr key={key} className="border-b border-gray-100 bg-red-50 hover:bg-red-100">
                                                    <td className="py-6 pr-6">
                                                        <RowTypeIndicator rowType={row.rowType} />
                                                    </td>
                                                    <td className="py-2 px-2">
                                                        <ActivityCell activity={row.prev} isEmpty={!row.prev} label="Opprinnelig" isLatest={false} />
                                                    </td>
                                                    <td className="py-2 px-2">
                                                        <ActivityCell activity={row.curr} isEmpty={!row.curr} label="Ny versjon" isLatest={true} />
                                                    </td>
                                                    <td className="py-6 pl-6">
                                                        {row.removedType === "deleted" ? (
                                                            <div className="text-red-500 font-medium">Slettet</div>
                                                        ) : (
                                                            <div className="text-orange-500 font-medium">Kansellert</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </>
                            )}
                            {/* Active Rows */}
                            {activeRows.map((row) => {
                                const key = row.curr?.activityId || row.prev?.activityId;
                                if (!key) return null;
                                const decision = decisions[key] || { decision: "pending", comment: "" };
                                return (
                                    <React.Fragment key={key}>
                                        <tr className="border-b border-gray-100 hover:bg-orange-50 transition-colors duration-150">
                                            <td className="py-6 pr-6">
                                                <RowTypeIndicator rowType={row.rowType} />
                                            </td>
                                            <td className="py-2 px-2">
                                                <ActivityCell activity={row.prev} isEmpty={!row.prev} label="Opprinnelig" isLatest={false} />
                                            </td>
                                            <td className="py-2 px-2">
                                                <ActivityCell activity={row.curr} isEmpty={!row.curr} label="Ny versjon" isLatest={true} />
                                            </td>
                                            <td className="py-6 pl-6">
                                                <div className="space-y-3">
                                                    <div className="flex items-center space-x-3">
                                                        <DecisionButton
                                                            decision="approved"
                                                            currentDecision={decision.decision}
                                                            onClick={() => setDecision(key, "approved")}
                                                            icon="ri-check-line"
                                                            label="Godkjenn"
                                                        />
                                                        <DecisionButton
                                                            decision="rejected"
                                                            currentDecision={decision.decision}
                                                            onClick={() => setDecision(key, "rejected")}
                                                            icon="ri-close-line"
                                                            label="Avvis"
                                                        />
                                                    </div>
                                                    {decision.decision === "pending" && (
                                                        <div className="flex justify-center items-center text-red-400">
                                                            <i className="ri-time-line mr-2 text-ms" />
                                                            <span className="text-ms font-bold">Avventer beslutning</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {decision.decision === "rejected" && (
                                            <tr className="bg-red-50 border-b border-red-100">
                                                <td className="py-4 pr-6"></td>
                                                <td colSpan={3} className="py-4 px-6">
                                                    <div className="max-w-2xl">
                                                        <label className="block text-sm text-red-600 mb-2 font-medium">Begrunnelse for avvisning</label>
                                                        <textarea
                                                            value={decision.comment}
                                                            onChange={(e) => setComment(key, e.target.value)}
                                                            placeholder="Skriv begrunnelse..."
                                                            className="w-full p-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white placeholder-gray-400"
                                                            rows={3}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}


                        </tbody>
                    </table>
                </div>

                {/* Global Comment */}
                <div className="mb-16 ">
                    <div className="flex items-center mb-6">
                        <i className="ri-chat-1-line mr-3 text-lg" />
                        <h3 className="text-lg font-medium ">Melding til kunden</h3>
                        <span className="ml-3 text-sm ">(valgfritt)</span>
                    </div>
                    <textarea
                        value={globalComment}
                        onChange={(e) => setGlobalComment(e.target.value)}
                        placeholder="Skriv en personlig melding til kunden om endringene..."
                        className="w-full p-6 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 resize-none bg-white placeholder-gray-400"
                        rows={4}
                    />
                </div>

                {/* Error */}
                {error && (
                    <div className="mb-16 max-w-3xl bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-start">
                            <i className="ri-alert-line text-red-600 mr-3 mt-0.5 text-lg" />
                            <div>
                                <h4 className="text-red-700 font-medium mb-2">Feil oppstod</h4>
                                <div className="text-red-600 text-sm whitespace-pre-line">
                                    {error}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="border-t border-gray-100 pt-12">
                    <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
                        {/* Bulk actions */}
                        <div className="flex gap-4">
                            <button
                                onClick={() => bulkAction("approve")}
                                className="flex items-center px-6 py-3 
                   bg-green-600 text-white 
                   border border-green-600 rounded-full 
                   hover:bg-green-700 hover:border-green-700 
                   font-medium text-sm 
                   transition-all duration-200 ease-in-out 
                   shadow-sm hover:shadow-md"
                            >
                                <i className="ri-check-line mr-2 text-sm" />
                                Godkjenn alle
                            </button>
                            <button
                                onClick={() => bulkAction("reject")}
                                className="flex items-center px-6 py-3 
                   bg-red-600 text-white 
                   border border-red-600 rounded-full 
                   hover:bg-red-700 hover:border-red-700 
                   font-medium text-sm 
                   transition-all duration-200 ease-in-out 
                   shadow-sm hover:shadow-md"
                            >
                                <i className="ri-close-line mr-2 text-sm" />
                                Avvis alle
                            </button>
                        </div>

                        {/* Final actions */}
                        <div className="flex gap-4">
                            <button
                                onClick={onCancel}
                                className="flex items-center px-8 py-3 
                   bg-white text-gray-600 
                   border border-gray-200 rounded-full 
                   hover:border-gray-900 hover:text-gray-900 
                   font-medium text-sm 
                   transition-all duration-200 ease-in-out 
                   shadow-sm hover:shadow-md"
                            >
                                <i className="ri-arrow-go-back-line mr-2 text-sm" />
                                Avbryt
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving || stats.pending > 0}
                                className={`flex items-center px-8 py-3 
                   rounded-full font-medium text-sm 
                   transition-all duration-200 ease-in-out 
                   shadow-sm ${saving || stats.pending > 0
                                        ? "bg-gray-300 text-white cursor-not-allowed"
                                        : "bg-gray-900 text-white hover:bg-gray-800 hover:shadow-md"
                                    }`}
                            >
                                {saving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                        Lagrer...
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-save-line mr-2 text-sm" />
                                        Send inn ({stats.approved} godkjent, {stats.rejected} avvist)
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {stats.pending > 0 && (
                        <div className="mt-8 bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <div className="flex items-center text-orange-700">
                                <i className="ri-time-line mr-2 text-sm" />
                                <span className="text-sm">
                                    Du har {stats.pending} aktivitet(er) som fortsatt venter på beslutning
                                </span>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}
